import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sun, Moon, Search, Menu, X, Twitter, Github, Linkedin, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BrandLogo, { BrandMark, BrandWordmark } from "@/components/BrandLogo";
import CookieConsent from "@/components/CookieConsent";
import { useTracking } from "@/hooks/useTracking";

const scrollTop = () => window.scrollTo({ top: 0, left: 0, behavior: "instant" });

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/analyze", label: "Analyze Channel" },
  { to: "/compare", label: "Compare Channels" },
  { to: "/rankings", label: "Rankings" },
  { to: "/blog", label: "Blog" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_COLS = [
  {
    title: "Product",
    links: [
      { label: "Analyze Channel", to: "/analyze" },
      { label: "Compare Channels", to: "/compare" },
      { label: "Rankings", to: "/rankings" },
      { label: "Dashboard", to: "/dashboard" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", to: "/blog" },
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Support", to: "/legal/support" },
      { label: "Sitemap", to: "/sitemap" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/legal/privacy" },
      { label: "Terms of Service", to: "/legal/terms" },
      { label: "Cookie Policy", to: "/legal/cookies" },
      { label: "Disclaimer", to: "/legal/disclaimer" },
    ],
  },
];

export default function Layout() {
  const [theme, setTheme] = useState(() => localStorage.getItem("yta-theme") || "light");
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useTracking();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    localStorage.setItem("yta-theme", theme);
  }, [theme]);

  // Scroll to top on every route change (fires only when pathname actually changes).
  useEffect(() => {
    scrollTop();
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate("/analyze", { state: { url: q.trim() } });
    setQ("");
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-logo" onClick={scrollTop} className="flex items-center gap-2.5 group shrink-0">
            <div className="group-hover:scale-105 transition-transform">
              <BrandMark size={36} />
            </div>
            <div className="block">
              <BrandWordmark compact />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 text-sm" aria-label="Primary">
            {NAV.slice(0, 5).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={scrollTop}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {NAV.slice(5).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={scrollTop}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden md:flex items-center relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3" />
              <Input
                data-testid="header-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="URL or channel name..."
                className="h-9 pl-9 pr-3 w-56 rounded-full bg-secondary/60 border-transparent focus-visible:bg-card focus-visible:border-border"
              />
            </form>
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              data-testid="header-cta-analyze"
              size="sm"
              onClick={() => navigate("/analyze")}
              className="btn-primary hidden sm:inline-flex h-9 px-5"
            >
              Get Started
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden rounded-full"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => { setMenuOpen(false); scrollTop(); }}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg font-medium text-sm ${isActive ? "bg-accent text-primary" : "text-foreground/80"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <form onSubmit={handleSearch} className="mt-2 relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="URL or channel name..." className="h-10 pl-9 rounded-full" />
              </form>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-border/60 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Row 1: brand + inline link groups */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <Link to="/" className="flex items-center gap-2.5 shrink-0" onClick={scrollTop}>
              <BrandMark size={28} />
              <BrandWordmark />
            </Link>

            <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              {FOOTER_COLS.flatMap((col) => col.links).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={scrollTop}
                  className="hover:text-primary transition-colors whitespace-nowrap"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2 shrink-0">
              {[
                { Icon: Twitter, name: "twitter" },
                { Icon: Github, name: "github" },
                { Icon: Linkedin, name: "linkedin" },
                { Icon: Youtube, name: "youtube" },
              ].map(({ Icon, name }) => (
                <a
                  key={name}
                  href="#"
                  className="w-7 h-7 rounded-full border border-border/70 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                  aria-label={`${name} link`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Row 2: copyright + contact */}
          <div className="mt-6 pt-4 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] font-mono text-muted-foreground">
            <div>© {new Date().getFullYear()} Creator Intelligence Tube · YouTube Data API v3 · GPT-5.2</div>
            <a
              href="mailto:creatorintelligencetube@gmail.com"
              data-testid="footer-email"
              className="hover:text-primary transition-colors"
            >
              creatorintelligencetube@gmail.com
            </a>
          </div>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
